import Add from "@mui/icons-material/Add";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FacilityPicker } from "components/FacilityPicker";
import AsyncAutocomplete from "components/Forms/AsyncAutocomplete";
import { CheckboxField } from "components/Forms/CheckboxField";
import { DateField } from "components/Forms/DateField";
import { FormSelect } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import {
  FacilityWasteStreamOptionsQuery,
  RcraInfoFederalWasteGeneratorStatus,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import { Control, Controller } from "react-hook-form";

import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";
import { FACILITY_WASTE_STREAM_OPTIONS_QUERY } from "./api";
import { generatorStatusSummary } from "./containerClockPreview";
import { UNIT_SELECT_ITEMS } from "./containerDisplay";
import {
  AreaOption,
  areaTypeLabel,
  AreaPickerOption,
  CONTAINER_TYPE_SELECT_ITEMS,
  FormSection,
  ReadOnlyFact,
  WasteContainerDetail,
  WasteContainerFormValues,
  WasteStreamOption,
} from "./containerFormParts";

export type {
  AreaOption,
  WasteContainerDetail,
  WasteContainerFormValues,
  WasteStreamOption,
} from "./containerFormParts";

export type ContainerFormFieldsProps = {
  mode: "create" | "edit";
  facilityId?: string | null;
  container?: WasteContainerDetail | null;
  control: Control<WasteContainerFormValues>;
  setValue: (
    name: keyof WasteContainerFormValues,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean }
  ) => void;
  selectedWasteStream: WasteStreamOption | null;
  setSelectedWasteStream: (stream: WasteStreamOption | null) => void;
  selectedArea: AreaOption | null;
  setSelectedArea: (area: AreaOption | null) => void;
  watchedFacilityId: string;
  watchedIsPermanent: boolean;
  watchedSqgOver200Miles: boolean;
  facilityContext?: { name?: string | null } | null;
  primaryEpaId?: string | null;
  tankPlacementLocked: boolean;
  visibleAreaOptions: AreaOption[];
  setNewAreaDialogOpen: (open: boolean) => void;
  setMoveDialogOpen: (open: boolean) => void;
  prefillCapacityForType: (typeCode: string) => void;
  generatorStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  submitAttempted: boolean;
};

function FacilitySections({
  mode,
  facilityId,
  control,
  setValue,
  setSelectedWasteStream,
  setSelectedArea,
  watchedFacilityId,
  facilityContext,
  primaryEpaId,
}: Pick<
  ContainerFormFieldsProps,
  | "mode"
  | "facilityId"
  | "control"
  | "setValue"
  | "setSelectedWasteStream"
  | "setSelectedArea"
  | "watchedFacilityId"
  | "facilityContext"
  | "primaryEpaId"
>) {
  return (
    <>
      {mode === "create" && !facilityId && (
        <FormSection label="Facility" first>
          <Grid size={{ xs: 12 }}>
            <Controller
              name="facilityId"
              control={control}
              render={({ field, fieldState }) => (
                <FacilityPicker
                  label="Facility"
                  facilityQueryVariant="waste"
                  textFieldProps={{ required: true }}
                  value={field.value || null}
                  onChange={(facility) => {
                    const nextFacilityId = facility?.id ?? "";
                    // A waste stream belongs to a facility; carrying the
                    // old selection across a facility switch dead-ends
                    // the save on "belongs to a different facility".
                    if (nextFacilityId !== field.value) {
                      setValue("wasteStreamId", null, { shouldValidate: true });
                      setSelectedWasteStream(null);
                      setValue("accumulationAreaId", null, { shouldValidate: true });
                      setSelectedArea(null);
                    }
                    field.onChange(nextFacilityId);
                  }}
                  {...fieldState}
                />
              )}
            />
          </Grid>
        </FormSection>
      )}
      {/* The facility is fixed for this form (either passed in or
                  picked above), so what it contributes to the container is
                  shown rather than asked for. */}
      {mode === "create" && watchedFacilityId && (
        <FormSection label="Facility information" first={!!facilityId}>
          <ReadOnlyFact label="Facility name" value={facilityContext?.name} />
          <ReadOnlyFact label="EPA ID" value={primaryEpaId} />
        </FormSection>
      )}
    </>
  );
}

function IdentitySection({
  mode,
  control,
  setValue,
  submitAttempted,
  prefillCapacityForType,
  tankPlacementLocked,
  selectedArea,
  setSelectedArea,
  watchedIsPermanent,
}: Pick<
  ContainerFormFieldsProps,
  | "mode"
  | "control"
  | "setValue"
  | "submitAttempted"
  | "prefillCapacityForType"
  | "tankPlacementLocked"
  | "selectedArea"
  | "setSelectedArea"
  | "watchedIsPermanent"
>) {
  return (
    <>
      {/* In create mode one of the two facility sections above always
                  renders, so Identity only leads the form when editing. */}
      <FormSection label="Identity" first={mode === "edit"}>
        <Grid size={{ xs: 12 }}>
          <FormTextField
            name="containerLabel"
            label="Container ID"
            control={control}
            helperText={mode === "create" ? "Leave blank to auto-generate a label" : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="containerType"
            control={control}
            render={({ field, fieldState }) => {
              const showError = submitAttempted || fieldState.isTouched || fieldState.isDirty;
              return (
                <TextField
                  select
                  fullWidth
                  required
                  label="Container type"
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    prefillCapacityForType(event.target.value);
                  }}
                  error={showError && !!fieldState.error}
                  helperText={showError ? fieldState.error?.message : undefined}
                >
                  {CONTAINER_TYPE_SELECT_ITEMS.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.display}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }}
          />
        </Grid>
        <Grid size={{ xs: 7 }}>
          <FormTextField name="capacity" label="Capacity" control={control} type="number" />
        </Grid>
        <Grid size={{ xs: 5 }}>
          <FormSelect
            name="capacityUnit"
            label="Capacity unit"
            control={control}
            selectItems={UNIT_SELECT_ITEMS}
          />
        </Grid>
        <VesselKindFields
          control={control}
          setValue={setValue}
          selectedArea={selectedArea}
          setSelectedArea={setSelectedArea}
          tankPlacementLocked={tankPlacementLocked}
          watchedIsPermanent={watchedIsPermanent}
        />
      </FormSection>
    </>
  );
}

/**
 * What the container IS, so it sits with the identity fields rather than among
 * the amounts. Flow-through follows permanent because it only means anything
 * for a permanent vessel.
 */
function VesselKindFields({
  control,
  setValue,
  selectedArea,
  setSelectedArea,
  tankPlacementLocked,
  watchedIsPermanent,
}: Pick<
  ContainerFormFieldsProps,
  | "control"
  | "setValue"
  | "selectedArea"
  | "setSelectedArea"
  | "tankPlacementLocked"
  | "watchedIsPermanent"
>) {
  return (
    <Grid size={{ xs: 12 }}>
      <CheckboxField
        name="isPermanent"
        label="Permanent vessel (tank) — emptied rather than archived"
        control={control}
        disabled={tankPlacementLocked}
        tooltip={
          tankPlacementLocked
            ? "Tanks can only be placed in central accumulation areas. This container is in a satellite area, so it can't be made permanent here."
            : undefined
        }
        checkboxProps={{
          onChange: (_event, checked) => {
            // Flow-through requires permanent; clear it when permanent
            // is unchecked, or the disabled flow-through checkbox holds
            // a stale true the user can no longer clear.
            if (!checked) {
              setValue("isFlowThrough", false, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
            // A tank can only sit in a central area — clear a
            // now-invalid satellite pick instead of leaving a stale
            // selection that would only surface as a critical issue.
            if (
              checked &&
              selectedArea &&
              selectedArea.type !== WasteAccumulationAreaType.Central
            ) {
              setSelectedArea(null);
              setValue("accumulationAreaId", null, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }
          },
        }}
        noErrorDisplay
      />
      <CheckboxField
        name="isFlowThrough"
        label="Flow-through (clock resets when emptied)"
        control={control}
        disabled={!watchedIsPermanent}
        noErrorDisplay
      />
    </Grid>
  );
}

function WasteSection({
  setValue,
  watchedFacilityId,
  selectedWasteStream,
  setSelectedWasteStream,
}: Pick<
  ContainerFormFieldsProps,
  "setValue" | "watchedFacilityId" | "selectedWasteStream" | "setSelectedWasteStream"
>) {
  return (
    <>
      <FormSection label="Waste">
        <Grid size={{ xs: 12 }}>
          <AsyncAutocomplete
            query={FACILITY_WASTE_STREAM_OPTIONS_QUERY}
            variables={{ facilityId: watchedFacilityId }}
            getItems={(data: FacilityWasteStreamOptionsQuery) =>
              data.facility?.wasteStreams?.items ?? []
            }
            getOptionLabel={(stream: WasteStreamOption) => stream.name}
            value={selectedWasteStream}
            onChange={(stream) => {
              setSelectedWasteStream(stream);
              setValue("wasteStreamId", stream?.id ?? null, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
            disabled={!watchedFacilityId}
            lazyLoad
            fullWidth
            // AsyncAutocomplete labels the field via placeholderLabel;
            // textFieldProps.label is overridden by it, so set this.
            placeholderLabel="Waste stream"
            initialSortModel={[{ field: "name", sort: "asc" }]}
          />
          {/* The stream's classification is what decides which limits the
                    container is measured against, so it is worth confirming at
                    the moment the stream is chosen rather than after saving. */}
          {selectedWasteStream && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.75 }}>
              Classification: {prettyPrintWasteClassification(selectedWasteStream.classification)}
            </Typography>
          )}
        </Grid>
        {/* The waste profile belongs to the stream, not the container,
                    so it is reported here rather than offered as a choice —
                    changing it is the stream's own form's job. */}
        {selectedWasteStream && (
          <ReadOnlyFact
            label="Waste profile #"
            value={selectedWasteStream.profile?.profileNumber}
          />
        )}
      </FormSection>
    </>
  );
}

/**
 * Where the container sits, plus the inline escape hatch to create the area it
 * belongs in. Satellite and central are governed by different rules — volume
 * versus time — so which one is picked changes what the clock will do, which
 * is what the helper text names.
 */
function AreaPickerField({
  setValue,
  watchedFacilityId,
  selectedArea,
  setSelectedArea,
  visibleAreaOptions,
  setNewAreaDialogOpen,
}: Pick<
  ContainerFormFieldsProps,
  | "setValue"
  | "watchedFacilityId"
  | "selectedArea"
  | "setSelectedArea"
  | "visibleAreaOptions"
  | "setNewAreaDialogOpen"
>) {
  return (
    <Autocomplete<AreaPickerOption>
      options={[
        ...visibleAreaOptions.map((area): AreaPickerOption => ({ kind: "area", area })),
        { kind: "create" },
      ]}
      getOptionLabel={(option) => (option.kind === "create" ? "+ New area" : option.area.name)}
      isOptionEqualToValue={(option, value) =>
        option.kind === "create"
          ? value.kind === "create"
          : value.kind === "area" && option.area.id === value.area.id
      }
      value={selectedArea ? { kind: "area", area: selectedArea } : null}
      disabled={!watchedFacilityId}
      filterOptions={(options, state) => {
        const query = state.inputValue.trim().toLowerCase();
        return options.filter(
          (option) => option.kind === "create" || option.area.name.toLowerCase().includes(query)
        );
      }}
      onChange={(_event, value) => {
        if (value?.kind === "create") {
          setNewAreaDialogOpen(true);
          return;
        }
        setSelectedArea(value?.area ?? null);
        setValue("accumulationAreaId", value?.area.id ?? null, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }}
      renderOption={(props, option) =>
        option.kind === "create" ? (
          <li {...props} key="create">
            <Add fontSize="small" sx={{ mr: 1 }} />
            New area
          </li>
        ) : (
          <li {...props} key={option.area.id}>
            {option.area.name} ({areaTypeLabel(option.area.type)})
          </li>
        )
      }
      renderInput={(params) => {
        let helperText: string | undefined;
        if (!watchedFacilityId) {
          helperText = "Select a facility first";
        } else if (selectedArea) {
          helperText = `Area type: ${areaTypeLabel(selectedArea.type)}`;
        }
        return <TextField {...params} label="Accumulation area" helperText={helperText} />;
      }}
    />
  );
}

function LocationSection({
  mode,
  container,
  setValue,
  watchedFacilityId,
  selectedArea,
  setSelectedArea,
  visibleAreaOptions,
  setNewAreaDialogOpen,
  setMoveDialogOpen,
  generatorStatus,
  watchedSqgOver200Miles,
}: Pick<
  ContainerFormFieldsProps,
  | "mode"
  | "container"
  | "setValue"
  | "watchedFacilityId"
  | "selectedArea"
  | "setSelectedArea"
  | "visibleAreaOptions"
  | "setNewAreaDialogOpen"
  | "setMoveDialogOpen"
  | "generatorStatus"
  | "watchedSqgOver200Miles"
>) {
  return (
    <>
      <FormSection label="Location">
        {mode === "create" && (
          <Grid size={{ xs: 12 }}>
            <AreaPickerField
              setValue={setValue}
              watchedFacilityId={watchedFacilityId}
              selectedArea={selectedArea}
              setSelectedArea={setSelectedArea}
              visibleAreaOptions={visibleAreaOptions}
              setNewAreaDialogOpen={setNewAreaDialogOpen}
            />
          </Grid>
        )}
        {mode === "edit" && container && (
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                label="Accumulation area"
                fullWidth
                disabled
                value={container.accumulationArea?.name ?? "Not yet placed"}
              />
              <Button
                size="small"
                sx={{ mt: 1, whiteSpace: "nowrap" }}
                onClick={() => setMoveDialogOpen(true)}
              >
                Move…
              </Button>
            </Stack>
          </Grid>
        )}
        {/* Derived, not chosen: generator status comes from the
                    facility's EPA registration or its shipment history, and it
                    is what decides how long this container may sit in central
                    accumulation — so the form states it where that consequence
                    is being set. */}
        {mode === "create" && watchedFacilityId && (
          <ReadOnlyFact
            label="Generator status (from facility)"
            value={generatorStatusSummary(generatorStatus, watchedSqgOver200Miles)}
          />
        )}
      </FormSection>
    </>
  );
}

function AmountSection({ mode, control }: Pick<ContainerFormFieldsProps, "mode" | "control">) {
  return (
    <>
      <FormSection label="Amount & timing">
        {mode === "create" && (
          <>
            <Grid size={{ xs: 7 }}>
              <FormTextField
                name="initialAmount"
                label="Current amount"
                control={control}
                type="number"
                helperText="Recorded as the container's first fill event"
              />
            </Grid>
            <Grid size={{ xs: 5 }}>
              <FormSelect
                name="initialAmountUnit"
                label="Unit"
                control={control}
                selectItems={UNIT_SELECT_ITEMS}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <DateField
                name="accumulationStartDate"
                label="Accumulation start date"
                control={control}
                datePickerProps={{ disableFuture: true }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <DateField
                name="movedToCentralDate"
                // Naming the consequence: this date is what the
                // central-accumulation deadline counts from.
                label="Moved to central (starts the clock)"
                control={control}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: mode === "create" ? 6 : 12 }}>
          <DateField name="estimatedPickupDate" label="Estimated pickup date" control={control} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CheckboxField
            name="isEstimate"
            label="Amount is an estimate"
            control={control}
            noErrorDisplay
          />
          <CheckboxField
            name="sqgOver200Miles"
            label="SQG shipping 200+ miles to the designated facility"
            control={control}
            noErrorDisplay
          />
        </Grid>
        {mode === "edit" && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                Amounts and status change through fill events, not from this form.
              </Typography>
            </Alert>
          </Grid>
        )}
      </FormSection>
    </>
  );
}

/**
 * Every field the container form asks for, grouped into its labelled sections.
 *
 * Split from {@link WasteContainerDialog}, which owns the form state, the
 * validation and the mutations — this half only renders what that state says.
 */
export function ContainerFormFields(props: ContainerFormFieldsProps) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
      <Grid container spacing={2}>
        <FacilitySections {...props} />
        <IdentitySection {...props} />
        <WasteSection {...props} />
        <LocationSection {...props} />
        <AmountSection {...props} />
      </Grid>
    </Box>
  );
}
